/** @format */

import PropTypes from "prop-types";
import Button from "./Button";

const ModalWrapper = ({
  heading = "Modal Title",
  size = "default",
  mobileMaxHeight = "90dvh",
  zIndex = 1300,
  simpleText,
  buttonText = null,
  buttonVariant = "main",
  showClose = true,
  onClose,
  onButtonClick,
  buttonType = "button",
  showCheckbox = false,
  checkboxLabel = "",
  checkboxChecked = false,
  checkboxError = "",
  onCheckboxChange,
  children,
}) => {
  return (
    <div
      className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-dis-scrim tw-p-6 tw-animate-connect-fade motion-reduce:tw-animate-none max-[640px]:tw-items-end max-[640px]:tw-p-0"
      style={{ zIndex }}
    >
      <div
        className={`tw-flex tw-max-h-[calc(100dvh-48px)] tw-w-[calc(100vw-48px)] ${
          size === "large" ? "tw-max-w-[600px]" : "tw-max-w-[480px]"
        } tw-flex tw-flex-col tw-overflow-hidden tw-rounded-[18px] tw-bg-white tw-text-dis-navy tw-shadow-dis-connect-desktop tw-animate-connect-pop max-[640px]:tw-fixed max-[640px]:tw-bottom-0 max-[640px]:tw-left-0 max-[640px]:tw-max-h-[var(--modal-mobile-max-height)] max-[640px]:tw-w-full max-[640px]:tw-rounded-t-[20px] max-[640px]:tw-rounded-b-none max-[640px]:tw-shadow-dis-connect-mobile max-[640px]:tw-animate-connect-up motion-reduce:tw-animate-none`}
        style={{ "--modal-mobile-max-height": mobileMaxHeight }}
      >
        <div className="tw-relative tw-flex tw-min-h-[66px] tw-flex-[0_0_auto] tw-items-center tw-justify-center tw-border-x-0 tw-border-t-0 tw-border-b tw-border-solid tw-border-dis-border tw-px-16 tw-py-2 max-[640px]:tw-min-h-[64px] max-[640px]:tw-px-[54px] max-[640px]:tw-py-1.5">
          <h2 className="tw-m-0 tw-text-center tw-text-[22px] tw-font-extrabold tw-leading-[1.2] tw-text-dis-navy max-[640px]:tw-text-lg">
            {heading}
          </h2>
          {showClose && (
            <button
              type="button"
              className="tw-absolute tw-right-3 tw-top-0 tw-bottom-0 tw-my-auto tw-inline-flex tw-h-11 tw-w-11 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-white tw-p-0 tw-text-dis-navy hover:tw-bg-dis-sky-tint focus-visible:tw-outline-2 focus-visible:tw-outline-dis-deep-blue focus-visible:tw-outline-offset-2"
              onClick={onClose}
              aria-label="Close"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false"
              >
                <path d="m6 6 12 12M18 6 6 18" />
              </svg>
            </button>
          )}
        </div>

        <div className="tw-no-scrollbar tw-flex tw-min-h-0 tw-flex-[1_1_auto] tw-flex-col tw-gap-4 tw-overflow-y-auto tw-overscroll-contain tw-px-7 tw-py-5 max-[640px]:tw-gap-3 max-[640px]:tw-px-[18px] max-[640px]:tw-py-4">
          {simpleText && (
            <div className="tw-shrink-0 tw-text-base tw-leading-[1.45] tw-text-dis-text-2">
              {simpleText}
            </div>
          )}
          {children}
          {showCheckbox && (
            <div className="tw-flex tw-shrink-0 tw-items-start tw-gap-3 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-dis-divider tw-pt-4">
              <input
                type="checkbox"
                className="tw-landing-native-checkbox tw-m-0 tw-h-6 tw-w-6 tw-min-w-6 tw-cursor-pointer tw-accent-dis-deep-blue tw-p-0 tw-shadow-none [&:checked::after]:tw-content-none"
                id="modalWrapperCheckbox"
                checked={checkboxChecked}
                onChange={onCheckboxChange}
              />
              <div className="tw-flex tw-flex-col tw-gap-2">
                <label
                  htmlFor="modalWrapperCheckbox"
                  className="tw-m-0 tw-flex tw-min-h-6 tw-cursor-pointer tw-items-center tw-text-[15px] tw-leading-[1.4] tw-text-dis-text-2 md:tw-text-base"
                >
                  {checkboxLabel}
                </label>
                {checkboxError && (
                  <span className="tw-text-sm tw-text-dis-error">
                    {checkboxError}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {buttonText && (
          <div className="tw-flex-[0_0_auto] tw-bg-white tw-px-7 tw-pb-6 tw-pt-2.5 max-[640px]:tw-px-[18px] max-[640px]:tw-pb-[calc(14px+env(safe-area-inset-bottom))] max-[640px]:tw-pt-1.5">
            <Button
              type={buttonType}
              variant={buttonVariant}
              size="large"
              fullWidth
              disabled={showCheckbox && !checkboxChecked}
              className="tw-min-h-14 tw-w-full"
              onClick={onButtonClick}
            >
              {buttonText}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

ModalWrapper.propTypes = {
  heading: PropTypes.string,
  size: PropTypes.oneOf(["default", "large"]),
  mobileMaxHeight: PropTypes.string,
  zIndex: PropTypes.number,
  simpleText: PropTypes.node,
  buttonText: PropTypes.string,
  buttonVariant: PropTypes.string,
  showClose: PropTypes.bool,
  onClose: PropTypes.func,
  onButtonClick: PropTypes.func,
  buttonType: PropTypes.string,
  showCheckbox: PropTypes.bool,
  checkboxLabel: PropTypes.node,
  checkboxChecked: PropTypes.bool,
  checkboxError: PropTypes.string,
  onCheckboxChange: PropTypes.func,
  children: PropTypes.node,
};

export default ModalWrapper;
